import { SENTRY_PROJECT_ID, SENTRY_URL } from "@/config";

import * as Sentry from "@sentry/react";

const BOOKING_JOURNEY_STORAGE_KEY = "volvamed_sentry_booking_journey";
const IMPERSONATION_STORAGE_KEY = "volvamed_sentry_impersonation";

let currentBookingId = "";
let currentIdentityKey;
let identitySyncInProgress = false;
let pendingIdentityState = null;
let replayLifecycle = "active";
let currentSessionContext = null;
let replayStopRetryTimer = null;
let replayStartPromise = null;

function requestReplayRotation() {
  if (replayLifecycle === "restart-pending") return;
  if (replayLifecycle === "stop-retry-pending") {
    replayLifecycle = "rotation-retry-pending";
    return;
  }
  replayLifecycle = "rotation-pending";
}

function markReplayRotationRetryPending() {
  replayLifecycle = "rotation-retry-pending";
}

function markReplayRestartPending() {
  replayLifecycle = "restart-pending";
}

function markReplayActive() {
  replayLifecycle = "active";
}

function markReplayStopRetryPending() {
  if (isReplayRotationPending()) {
    markReplayRotationRetryPending();
    return;
  }
  replayLifecycle = "stop-retry-pending";
}

function markReplayDisabledForImpersonation() {
  replayLifecycle = "disabled-for-impersonation";
}

function leaveReplayImpersonation() {
  if (replayLifecycle === "disabled-for-impersonation") {
    markReplayRestartPending();
  }
}

function isReplayRotationPending() {
  return ["rotation-pending", "rotation-retry-pending"].includes(
    replayLifecycle
  );
}

function isReplayRotationRetryPending() {
  return replayLifecycle === "rotation-retry-pending";
}

function isReplayRestartPending() {
  return replayLifecycle === "restart-pending";
}

function isReplayDisabledForImpersonation() {
  return replayLifecycle === "disabled-for-impersonation";
}

function readSessionValue(key) {
  let storedValue = null;
  try {
    storedValue = sessionStorage.getItem(key);
  } catch {
    return null;
  }
  if (storedValue === null) return null;

  try {
    const value = JSON.parse(storedValue);
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      try {
        sessionStorage.removeItem(key);
      } catch {
        // Observability storage must never interrupt the application.
      }
      return null;
    }
    return value;
  } catch {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Observability storage must never interrupt the application.
    }
    return null;
  }
}

function getSentryImpersonation() {
  if (currentSessionContext?.mode === "normal") return null;
  if (
    currentSessionContext?.mode === "impersonation" &&
    currentSessionContext.actor_admin_id
  ) {
    return { admin_id: String(currentSessionContext.actor_admin_id) };
  }
  const impersonation = readSessionValue(IMPERSONATION_STORAGE_KEY);
  if (!impersonation?.admin_id) {
    if (impersonation) sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    return null;
  }
  return { admin_id: String(impersonation.admin_id) };
}

function applyBookingJourneyTags() {
  const journey = getSentryBookingJourney();
  try {
    Sentry.setTag("booking_journey_id", journey?.id || undefined);
  } catch {
    // Keep applying independent tags after an SDK failure.
  }
  try {
    Sentry.setTag("booking_therapist_id", journey?.therapist_id || undefined);
  } catch {
    // Observability failures must never interrupt the application.
  }
}

function applyImpersonationTags() {
  let impersonation = null;
  try {
    impersonation = getSentryImpersonation();
  } catch {
    // Storage failures must not prevent independent tag cleanup.
  }
  try {
    Sentry.setTag("session_mode", impersonation ? "impersonation" : undefined);
  } catch {
    // Keep applying independent tags after an SDK failure.
  }
  try {
    Sentry.setTag("actor_admin_id", impersonation?.admin_id || undefined);
  } catch {
    // Sentry failures must never interrupt authentication.
  }
}

function scheduleReplayStopRetry() {
  if (replayStopRetryTimer !== null) return;
  replayStopRetryTimer = window.setTimeout(async () => {
    replayStopRetryTimer = null;
    if (!getSentryImpersonation()) return;
    const stopped = await stopReplayForImpersonation();
    if (!stopped) scheduleReplayStopRetry();
  }, 1000);
}

async function stopReplayForImpersonation() {
  if (isReplayDisabledForImpersonation()) return true;
  let replay = null;
  try {
    replay = Sentry.getReplay();
  } catch {
    markReplayStopRetryPending();
    scheduleReplayStopRetry();
    return false;
  }
  if (!replay) {
    markReplayDisabledForImpersonation();
    return true;
  }
  try {
    // Impersonation must wait for recording to stop, not for a Replay upload.
    await replay.stop({ flush: false });
    markReplayDisabledForImpersonation();
    return true;
  } catch {
    markReplayStopRetryPending();
    scheduleReplayStopRetry();
    return false;
  }
}

function resolveActiveAccount({ activeRole, admin, customer, therapist }) {
  const accounts = { admin, customer, therapist };
  if (!activeRole || !accounts[activeRole]?._id) return null;
  return { role: activeRole, user: accounts[activeRole] };
}

function applyIdentity(account) {
  if (!account) {
    Sentry.setUser(null);
    Sentry.setTag("account_role", undefined);
    return;
  }

  Sentry.setUser({
    id: `${account.role}:${account.user._id}`,
  });
  Sentry.setTag("account_role", account.role);
}

async function applyIdentityWithRotation(account, shouldRotate) {
  if (shouldRotate) requestReplayRotation();

  let replay = null;
  if (isReplayRotationPending() || isReplayRestartPending()) {
    try {
      replay = Sentry.getReplay();
    } catch {
      if (isReplayRotationPending()) markReplayRotationRetryPending();
      return;
    }
  }

  if (isReplayRotationRetryPending() && !replay) return;
  if (isReplayRotationPending() && !replay) markReplayActive();

  if (isReplayRotationPending()) {
    try {
      await replay.stop();
      markReplayRestartPending();
    } catch {
      markReplayRotationRetryPending();
      return;
    }
  }

  applyIdentity(account);
  applyBookingJourneyTags();
  applyImpersonationTags();
  Sentry.setTag("booking_id", currentBookingId || undefined);

  if (!replay || !isReplayRestartPending()) return;
  if (getSentryImpersonation()) return;
  try {
    replayStartPromise = replay.start();
    await replayStartPromise;
    markReplayActive();
  } catch {
    markReplayRestartPending();
  } finally {
    replayStartPromise = null;
  }
}

async function applyIdentityState(state) {
  if (state?.sessionContext) setSentrySessionContext(state.sessionContext);
  const impersonation = getSentryImpersonation();
  const account = impersonation
    ? {
        role: "admin",
        user: { _id: impersonation.admin_id },
      }
    : resolveActiveAccount(state);
  const nextIdentityKey = account
    ? `${account.role}:${account.user._id}`
    : "anonymous";
  const hasBookingJourney = Boolean(getSentryBookingJourney());

  if (impersonation) {
    currentIdentityKey = nextIdentityKey;
    applyIdentity(account);
    applyBookingJourneyTags();
    applyImpersonationTags();
    Sentry.setTag("booking_id", currentBookingId || undefined);
    if (isReplayDisabledForImpersonation()) return;
    await stopReplayForImpersonation();
    return;
  }

  if (isReplayDisabledForImpersonation()) leaveReplayImpersonation();
  const shouldRotate =
    currentIdentityKey !== undefined &&
    currentIdentityKey !== nextIdentityKey &&
    !(
      currentIdentityKey === "anonymous" &&
      account?.role === "customer" &&
      hasBookingJourney
    );

  if (currentIdentityKey === nextIdentityKey) {
    if (isReplayRotationPending() || isReplayRestartPending()) {
      await applyIdentityWithRotation(account, false);
      return;
    }
    applyIdentity(account);
    applyBookingJourneyTags();
    applyImpersonationTags();
    return;
  }

  currentIdentityKey = nextIdentityKey;
  await applyIdentityWithRotation(account, shouldRotate);
}

export function getSentryBookingJourney() {
  const journey = readSessionValue(BOOKING_JOURNEY_STORAGE_KEY);
  if (!journey?.id || !journey?.therapist_id) {
    if (journey) {
      try {
        sessionStorage.removeItem(BOOKING_JOURNEY_STORAGE_KEY);
      } catch {
        // Observability storage must never interrupt the application.
      }
    }
    return null;
  }
  return {
    id: String(journey.id),
    therapist_id: String(journey.therapist_id),
  };
}

export function startSentryBookingJourney(therapistId) {
  const normalizedTherapistId = String(therapistId || "");
  if (!normalizedTherapistId) return null;

  const currentJourney = getSentryBookingJourney();
  if (currentJourney?.therapist_id === normalizedTherapistId) {
    applyBookingJourneyTags();
    return currentJourney;
  }

  const journey = {
    id: crypto.randomUUID(),
    therapist_id: normalizedTherapistId,
  };
  try {
    sessionStorage.setItem(
      BOOKING_JOURNEY_STORAGE_KEY,
      JSON.stringify(journey)
    );
  } catch {
    // The booking flow continues even when observability storage is unavailable.
  }
  clearSentryBooking();
  applyBookingJourneyTags();
  return journey;
}

export function finishSentryBookingJourney() {
  try {
    sessionStorage.removeItem(BOOKING_JOURNEY_STORAGE_KEY);
  } catch {
    // Observability storage must never interrupt the application.
  }
  try {
    Sentry.setTag("booking_journey_id", undefined);
  } catch {
    // Keep clearing independent tags after an SDK failure.
  }
  try {
    Sentry.setTag("booking_therapist_id", undefined);
  } catch {
    // Observability failures must never interrupt the application.
  }
}

export function setSentryBooking(bookingId) {
  currentBookingId = String(bookingId || "");
  try {
    Sentry.setTag("booking_id", currentBookingId || undefined);
  } catch {
    // Observability failures must never affect a successful booking.
  }
}

export function clearSentryBooking(bookingId) {
  if (bookingId && String(bookingId) !== currentBookingId) return;
  currentBookingId = "";
  try {
    Sentry.setTag("booking_id", undefined);
  } catch {
    // Observability failures must never affect booking cleanup.
  }
}

export async function flushSentryReplay() {
  try {
    const replay = Sentry.getReplay();
    if (!replay) return;
    await replay.flush();
  } catch {
    // Observability failures must not affect the booking flow.
  }
}

export function startSentryImpersonation(adminId) {
  const normalizedAdminId = String(adminId || "");
  if (!normalizedAdminId) return;
  currentSessionContext = {
    mode: "impersonation",
    actor_admin_id: normalizedAdminId,
    replay_enabled: false,
  };
  try {
    sessionStorage.setItem(
      IMPERSONATION_STORAGE_KEY,
      JSON.stringify({ admin_id: normalizedAdminId })
    );
  } catch {
    // Observability storage must never interrupt authentication.
  }
  try {
    applyImpersonationTags();
  } catch {
    // Sentry failures must never interrupt authentication.
  }
}

export async function prepareSentryImpersonation(adminId) {
  const normalizedAdminId = String(adminId || "");
  if (!normalizedAdminId) return false;
  startSentryImpersonation(normalizedAdminId);
  applyIdentity({ role: "admin", user: { _id: normalizedAdminId } });
  applyBookingJourneyTags();
  applyImpersonationTags();
  Sentry.setTag("booking_id", currentBookingId || undefined);
  if (replayStartPromise) {
    try {
      await replayStartPromise;
    } catch {
      // Replay still has to be stopped after a failed start attempt.
    }
  }
  return stopReplayForImpersonation();
}

export async function prepareSentrySessionContext(context) {
  if (context?.mode === "impersonation" && context.actor_admin_id) {
    return prepareSentryImpersonation(context.actor_admin_id);
  }
  setSentrySessionContext(context);
  return true;
}

export function clearSentryImpersonation() {
  currentSessionContext = {
    mode: "normal",
    actor_admin_id: "",
    replay_enabled: true,
  };
  if (replayStopRetryTimer !== null) {
    window.clearTimeout(replayStopRetryTimer);
    replayStopRetryTimer = null;
  }
  leaveReplayImpersonation();
  try {
    sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  } catch {
    // Observability storage must never interrupt authentication.
  }
  try {
    Sentry.setTag("session_mode", undefined);
  } catch {
    // Sentry failures must never interrupt authentication.
  }
  try {
    Sentry.setTag("actor_admin_id", undefined);
  } catch {
    // Keep clearing independent tags after an SDK failure.
  }
}

export function setSentrySessionContext(context) {
  if (context?.mode === "impersonation" && context.actor_admin_id) {
    startSentryImpersonation(context.actor_admin_id);
    return;
  }
  clearSentryImpersonation();
}

export function restoreSentrySessionContext(sessionContext) {
  if (sessionContext) setSentrySessionContext(sessionContext);
  try {
    applyBookingJourneyTags();
  } catch {
    // Keep restoring independent context after an observability failure.
  }
  try {
    applyImpersonationTags();
  } catch {
    // Keep restoring independent context after an observability failure.
  }
  try {
    Sentry.setTag("booking_id", currentBookingId || undefined);
  } catch {
    // Observability failures must never interrupt application startup.
  }
}

export async function syncSentryIdentity(state) {
  pendingIdentityState = state;
  if (identitySyncInProgress) return;

  identitySyncInProgress = true;
  try {
    while (pendingIdentityState) {
      const nextIdentityState = pendingIdentityState;
      pendingIdentityState = null;
      try {
        await applyIdentityState(nextIdentityState);
      } catch {
        // Keep draining newer identity states after an SDK failure.
      }
    }
  } finally {
    identitySyncInProgress = false;
  }
}

export function buildSentryReplayUrl(query) {
  const url = new URL(SENTRY_URL);
  url.searchParams.set("project", SENTRY_PROJECT_ID);
  url.searchParams.set("query", query);
  url.searchParams.set("statsPeriod", "90d");
  return url.toString();
}

export function openSentryReplays(query) {
  window.open(buildSentryReplayUrl(query), "_blank", "noopener,noreferrer");
}
